import type { ComboboxGroupedOption, ComboboxOption } from "@sentricmusic/ui";

import { Button, Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Text } from "@sentricmusic/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUIStateStore } from "@/store/uiStateStore";

export type TrackInfo = {
  id: string;
  name: string;
  artistName?: string;
  isrc?: string;
};

export type LinkedSongInfo = {
  workCode: string;
  title: string;
};

export type LinkType = "account" | "album";

type LinkToExistingSongModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  track: TrackInfo | null;
  accountSongOptions: ComboboxOption[];
  albumTrackOptions: ComboboxOption[]; // Only original tracks (works), not variants
  onConfirmAccountLink: (trackId: string, workCode: string, workTitle: string) => void;
  onConfirmAlbumLink: (trackId: string, targetTrackId: string, targetTrackTitle: string) => void;
  isLoading?: boolean;
};

const LinkToExistingSongModal: React.FC<LinkToExistingSongModalProps> = ({
  open,
  onOpenChange,
  track,
  accountSongOptions,
  albumTrackOptions,
  onConfirmAccountLink,
  onConfirmAlbumLink,
  isLoading = false,
}) => {
  const { t } = useTranslation(["album", "common"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [selectedValue, setSelectedValue] = useState<string>("");

  // Create grouped options for the combobox
  const groupedOptions: ComboboxGroupedOption[] = useMemo(() => {
    const groups: ComboboxGroupedOption[] = [];

    // Add album tracks group if there are any
    if (albumTrackOptions.length > 0) {
      groups.push({
        groupLabel: t("album:in-this-album") ?? "In This Album",
        options: albumTrackOptions.map(opt => ({
          ...opt,
          value: `album:${opt.value}`, // Prefix to distinguish from account options
        })),
      });
    }

    // Add account songs group if there are any
    if (accountSongOptions.length > 0) {
      groups.push({
        groupLabel: t("album:in-your-account") ?? "In Your Account",
        options: accountSongOptions.map(opt => ({
          ...opt,
          value: `account:${opt.value}`, // Prefix to distinguish from album options
        })),
      });
    }

    return groups;
  }, [albumTrackOptions, accountSongOptions, t]);

  // Parse the selected value to determine type and actual value
  const parseSelectedValue = (value: string): { type: LinkType; id: string } | null => {
    if (value.startsWith("album:")) {
      return { type: "album", id: value.replace("album:", "") };
    }
    if (value.startsWith("account:")) {
      return { type: "account", id: value.replace("account:", "") };
    }
    return null;
  };

  // Get the selected option for display
  const selectedOption = useMemo(() => {
    const parsed = parseSelectedValue(selectedValue);
    if (!parsed)
      return null;

    if (parsed.type === "album") {
      return albumTrackOptions.find(opt => opt.value === parsed.id);
    }
    return accountSongOptions.find(opt => opt.value === parsed.id);
  }, [selectedValue, albumTrackOptions, accountSongOptions]);

  const selectedLinkType = parseSelectedValue(selectedValue)?.type ?? null;

  const handleConfirm = () => {
    if (!track || !selectedValue || !selectedOption)
      return;

    const parsed = parseSelectedValue(selectedValue);
    if (!parsed)
      return;

    // Extract just the song title (before the artist part)
    const title = selectedOption.label.split(" - ")[0] || selectedOption.label;

    if (parsed.type === "album") {
      onConfirmAlbumLink(track.id, parsed.id, title);
    }
    else {
      onConfirmAccountLink(track.id, parsed.id, title);
    }

    setSelectedValue("");
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedValue("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("album:link-to-existing-song") ?? "Link to Existing Song"}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Side-by-side comparison */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Left Card - Existing Song Selection */}
            <div className="p-4 border rounded-lg border-stroke-subtle bg-layer-disabled">
              <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                {t("album:existing-song") ?? "Existing Song"}
              </Text>

              {selectedOption ? (
                <>
                  <Text as="p" className="text-lg font-semibold">
                    {selectedOption.label.split(" - ")[0] || selectedOption.label}
                  </Text>
                  {selectedOption.label.includes(" - ") && (
                    <Text as="p" className="text-sm text-ink-secondary">
                      {selectedOption.label.split(" - ").slice(1).join(" - ")}
                    </Text>
                  )}
                  <Text as="p" className="mt-2 text-xs italic text-ink-secondary">
                    {selectedLinkType === "album"
                      ? (t("album:on-this-album-indicator") ?? "On this album")
                      : (t("album:already-in-account-indicator") ?? "Already in account")}
                  </Text>
                </>
              ) : (
                <Text as="p" className="text-sm italic text-ink-secondary">
                  {t("album:select-song-to-link") ?? "Select a song to link this track to"}
                </Text>
              )}
            </div>

            {/* Right Card - Track Being Added */}
            <div className="p-4 border-2 rounded-lg border-stroke-primary bg-action-primary/5">
              <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                {t("album:new-recording") ?? "New Recording"}
              </Text>
              {track ? (
                <>
                  <Text as="p" className="text-lg font-semibold">
                    {track.name}
                  </Text>
                  {typeof track.artistName === "string" && track.artistName.length > 0 && (
                    <Text as="p" className="text-sm text-ink-secondary">
                      {track.artistName}
                    </Text>
                  )}
                  {typeof track.isrc === "string" && track.isrc.length > 0 && (
                    <Text as="p" className="mt-2 text-xs text-ink-secondary">
                      ISRC:
                      {" "}
                      {track.isrc}
                    </Text>
                  )}
                </>
              ) : (
                <Text as="p" className="text-sm italic text-ink-secondary">
                  No track selected
                </Text>
              )}
            </div>
          </div>

          {/* Song Selection Combobox */}
          <Combobox
            smallFont
            label={t("album:select-existing-song-label") ?? "Select existing song:"}
            data={groupedOptions}
            onValueChange={(value) => {
              if (typeof value === "string") {
                setSelectedValue(value);
              }
            }}
            value={selectedValue}
            placeholder={t("album:select-song-placeholder") ?? "Select song..."}
            searchPlaceholder={t("album:search-existing-song") ?? "Search for existing song..."}
            emptyText={t("album:no-songs-found2") ?? "No songs found"}
            searchByLabel
            disabled={isLoading}
          />

          {/* Explanation Text */}
          <Text as="p" className="text-sm text-ink-secondary">
            {t("album:link-recording-explanation") ?? "By linking this track to an existing song, it will be attached as a recording of that song. The writers and shares from the existing song will apply to this recording."}
          </Text>
        </div>

        <DialogFooter>
          <Button
            variant={isTunecore ? "primary" : "secondary"}
            label={t("common:cancel") ?? "Cancel"}
            onClick={handleCancel}
            disabled={isLoading}
          />
          <Button
            variant={isTunecore ? "secondary" : "primary"}
            label={t("album:link-as-recording") ?? "Link as Recording"}
            onClick={handleConfirm}
            disabled={!selectedValue || isLoading}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkToExistingSongModal;
